import {
	get,
	put,
	post,
	axiosErrorHandler,
} from 'models/shared/helpers/axiosInstance';
import { PaginatedDocuments } from 'models/shared/types/pagination';
import moment from 'moment';
import {
	Repossession,
	RepossessionLocation,
	RepossessionUpdateDTO,
} from '../interfaces/repossession.interface';
import {
	AdditionRecoveriesDto,
	CreateRecoveriesDto,
	Recovery,
	RecoveryUpdateDto,
} from '../interfaces/recovery.interface';
import { Route, RouteUpdateDto } from '../interfaces/route.interface';
import {
	RepossessionEvidenceType,
	RepossessionPolicy,
} from '../enums/repossession.enums';

export const FindAllRepossessions = async (
	sort?: string,
	page?: number,
	limit?: number,
	query?: string,
	policy?: string,
): Promise<PaginatedDocuments<Repossession>> => {
	try {
		const res = await get<PaginatedDocuments<Repossession>>(
			`/v1/repossession?page=${page || 1}&limit=${limit || 30}${
				query ? `&query=${query}` : ''
			}${sort ? `&sort=${sort}` : ''}${policy ? `&policy=${policy}` : ''}`,
		);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const findRepossessionByLoan = async (
	id: string,
): Promise<Repossession[]> => {
	try {
		const res = await get<Repossession[]>(`/v1/repossession/loan/${id}`);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const FetchLocations = async (): Promise<RepossessionLocation[]> => {
	try {
		const res = await get<RepossessionLocation[]>('/v1/repossession/locations');
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};
export const FetchRepossession = async (id: string): Promise<Repossession> => {
	try {
		const res = await get<Repossession>(`/v1/repossession/${id}`);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const CreateRepossession = async (
	loan: string,
	policy: RepossessionPolicy,
): Promise<Repossession> => {
	try {
		const res = await post('/v1/repossession', {
			loan,
			policy,
		});
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};
export const CreateCreditRepossessionDevolution = async (
	id: string,
	addressData: { zipCode: string; street: string; extNumber: string },
): Promise<Repossession> => {
	try {
		const res = await post('/v1/repossession/devolution', {
			creditId: id,
			...addressData,
		});
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};
export const updateRepossession = async (
	id: string,
	data: RepossessionUpdateDTO,
): Promise<Repossession> => {
	try {
		const res = await put(`/v1/repossession/${id}`, data);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const uploadFileRepossession = async (
	id: string,
	file: File,
	type: RepossessionEvidenceType,
): Promise<Repossession> => {
	try {
		const data = new FormData();
		data.append('name', file.name);
		data.append('file', file);

		const res = await put<Repossession>(
			`/v1/repossession/${id}/documents/${type}`,
			data,
			{
				headers: { 'Content-Type': 'multipart/form-data' },
			},
		);
		return res.data;
	} catch (err: any) {
		throw axiosErrorHandler(err);
	}
};

export const FetchRoutes = async (
	page: number,
	status?: string[],
	dates?: { start: Date; end: Date },
	team?: string[],
): Promise<PaginatedDocuments<Route>> => {
	try {
		const res = await get<PaginatedDocuments<Route>>(
			// eslint-disable-next-line quotes
			`/v1/route?page=${page}${
				dates
					? `&startDate=${moment(dates.start).format(
							'YYYY-MM-DD',
					  )}&endDate=${moment(dates.end).format('YYYY-MM-DD')}`
					: ''
			}${team?.length ? `&team=${team.join(',')}` : ''}${
				status?.length ? `&status=${status.join(',')}` : ''
			}`,
		);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};
export const FetchRoute = async (id: string): Promise<Route> => {
	try {
		const res = await get<Route>(
			// eslint-disable-next-line quotes
			`/v1/route/${id}`,
		);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};
export const FetchRecovery = async (id: string): Promise<Recovery> => {
	try {
		const res = await get<Recovery>(`/v1/recovery?${id}`);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};
export const FetchRecoveries = async (
	idRoute: string,
): Promise<PaginatedDocuments<Recovery>> => {
	try {
		const res = await get<PaginatedDocuments<Recovery>>(
			`/v1/recovery?route=${idRoute}`,
		);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};
export const FetchRecoveriesByLoan = async (
	idLoan: string,
): Promise<Recovery[]> => {
	try {
		const res = await get<Recovery[]>(`/v1/recovery/loan/${idLoan}`);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const updateRecovery = async (
	id: string,
	data: RecoveryUpdateDto,
): Promise<Recovery> => {
	try {
		const res = await put<Recovery>(`/v1/recovery/${id}`, data);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const updateRoute = async (
	id: string,
	data: RouteUpdateDto,
): Promise<Route> => {
	try {
		const res = await put<Route>(`/v1/route/${id}`, data);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const createRoute = async (
	data: CreateRecoveriesDto,
): Promise<{
	message: string;
}> => {
	try {
		const res = await post<{
			message: string;
		}>('/v1/recovery/process-creation', data);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const additionToRoute = async (
	data: AdditionRecoveriesDto,
): Promise<{
	message: string;
}> => {
	try {
		const res = await post<{
			message: string;
		}>('/v1/recovery/process-addition', data);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};
